import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  avatarColorForId,
  clamp,
  DAY_LABELS,
  END_HOUR,
  fmtNoShort,
  getDaySchedule,
  planDayKey,
  hasOverlap,
  initials,
  slotToTime,
  START_HOUR,
  TOTAL_SLOTS,
} from "./schedule-utils";

function EventBlock({ ev, conflict }) {
  const { t } = useTranslation();
  const kind = ev.kind || "busy";
  const top = ev.allDay ? 0 : `calc(var(--slot-h) * ${ev.startSlot})`;
  const height = ev.allDay
    ? `calc(var(--slot-h) * ${ev.endSlot - ev.startSlot})`
    : `calc(var(--slot-h) * ${ev.endSlot - ev.startSlot} - 2px)`;
  return (
    <div
      className={`ra-event ${kind}${conflict ? " conflict-warn" : ""}`}
      style={{ top, height }}
    >
      <div className="ev-title">{ev.title}</div>
      <div className="ev-time">
        {ev.allDay
          ? t("work_order.event.all_day")
          : `${slotToTime(ev.startSlot)} – ${slotToTime(ev.endSlot)}`}
        {!ev.allDay && ev.customer ? ` · ${ev.customer}` : ""}
      </div>
    </div>
  );
}

function DraftBlock({
  apt,
  onMoveDown,
  onResizeDown,
  dragging,
  t,
  blockTitle,
  onRemove,
}) {
  const top = `calc(var(--slot-h) * ${apt.startSlot})`;
  const height = `calc(var(--slot-h) * ${apt.endSlot - apt.startSlot} - 2px)`;
  const mins = (apt.endSlot - apt.startSlot) * 15;
  const hrs = Math.floor(mins / 60);
  const durLabel =
    hrs > 0 ? `${hrs}t${mins % 60 ? ` ${mins % 60}m` : ""}` : `${mins}m`;

  return (
    <div
      className={`ra-event draft${dragging ? " dragging" : ""}`}
      style={{ top, height }}
      onPointerDown={onMoveDown}
      onContextMenu={
        onRemove
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }
          : undefined
      }
      role="presentation"
    >
      {onRemove ? (
        <button
          type="button"
          className="ra-draft-remove"
          title={t("work_order.plan_modal.remove_day")}
          aria-label={t("work_order.plan_modal.remove_day")}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
        >
          ✕
        </button>
      ) : null}
      <div className="ev-title">
        {blockTitle || t("report.agreement.new")} · {durLabel}
      </div>
      <div className="ev-time">
        {slotToTime(apt.startSlot)} – {slotToTime(apt.endSlot)}
      </div>
      <div className="resize-handle" onPointerDown={onResizeDown} role="presentation" />
    </div>
  );
}

export default function ReportAgreementWeekCalendar({
  employee,
  weekStart,
  weekLabel,
  onWeekChange,
  onToday,
  appointment,
  setAppointment,
  existing,
  absenceDays = [],
  showWeekend = true,
  isLoading,
  /** When set, new selections use this slot span (e.g. hours × 4). */
  fixedDurationSlots = null,
  draftBlockTitle,
  planSelectionMode = false,
  planSelection = null,
  onPlanSelectionChange,
  /** Optional node rendered at the very top of the calendar column (e.g. user tabs). */
  topBar = null,
  /** When set (plan mode), called with the day key to remove a planned day. */
  onRemovePlanDay = null,
  /** When set (0–6), show only that weekday column (mobile day view). */
  focusDay = null,
  className = "",
}) {
  const { t } = useTranslation();
  const weekDayCount = showWeekend ? 7 : 5;
  const visibleDayIndices =
    focusDay != null && focusDay >= 0 && focusDay < weekDayCount
      ? [focusDay]
      : Array.from({ length: weekDayCount }, (_, i) => i);
  const singleDay = visibleDayIndices.length === 1;
  const dayRefs = useRef([]);
  const [dragState, setDragState] = useState(null);

  const getSlotH = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--slot-h");
    return parseFloat(v) || 14;
  };

  const hitTest = (e) => {
    const x = e.clientX;
    let dayIdx = -1;
    for (const i of visibleDayIndices) {
      const ref = dayRefs.current[i];
      if (!ref) continue;
      const r = ref.getBoundingClientRect();
      if (x >= r.left && x <= r.right) {
        dayIdx = i;
        break;
      }
    }
    if (dayIdx === -1) {
      if (singleDay) {
        dayIdx = visibleDayIndices[0];
      } else {
        let best = visibleDayIndices[0] ?? 0;
        let bestDist = Infinity;
        for (const i of visibleDayIndices) {
          const ref = dayRefs.current[i];
          if (!ref) continue;
          const r = ref.getBoundingClientRect();
          const c = (r.left + r.right) / 2;
          const d = Math.abs(x - c);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }
        dayIdx = best;
      }
    }
    const ref = dayRefs.current[dayIdx];
    const r = ref.getBoundingClientRect();
    const y = e.clientY - r.top;
    const slot = clamp(Math.floor(y / getSlotH()), 0, TOTAL_SLOTS - 1);
    return { dayIdx, slot };
  };

  const durationSlots =
    fixedDurationSlots != null
      ? clamp(Math.round(Number(fixedDurationSlots)), 1, TOTAL_SLOTS)
      : null;

  const startDrag = (initial) => {
    setDragState(initial);
    let current = initial;
    const onMove = (ev) => {
      if (ev.pointerType === "touch") ev.preventDefault();
      const hit = hitTest(ev);
      if (current.mode === "create") {
        if (durationSlots != null) {
          const ns = clamp(hit.slot, 0, TOTAL_SLOTS - durationSlots);
          const startDay = Math.min(current.startDay ?? current.day, hit.dayIdx);
          const endDay = Math.max(current.startDay ?? current.day, hit.dayIdx);
          current = {
            ...current,
            day: hit.dayIdx,
            startDay,
            endDay,
            startSlot: ns,
            endSlot: ns + durationSlots,
          };
        } else {
          const a = Math.min(current.anchorSlot, hit.slot);
          const b = Math.max(current.anchorSlot, hit.slot);
          current = {
            ...current,
            day: hit.dayIdx,
            startSlot: a,
            endSlot: Math.max(a + 1, b + 1),
          };
        }
      } else if (current.mode === "move") {
        const dur = current.endSlot - current.startSlot;
        const delta = hit.slot - current.anchorSlot;
        let ns = current.origStart + delta;
        ns = clamp(ns, 0, TOTAL_SLOTS - dur);
        current = {
          ...current,
          day: hit.dayIdx,
          startSlot: ns,
          endSlot: ns + dur,
        };
      } else if (current.mode === "resize") {
        const ne = clamp(
          Math.max(hit.slot + 1, current.startSlot + 1),
          current.startSlot + 1,
          TOTAL_SLOTS
        );
        current = { ...current, endSlot: ne };
      }
      setDragState(current);
    };
    const onUp = () => {
      if (planSelectionMode && onPlanSelectionChange) {
        const startDay = Math.min(
          current.startDay ?? current.day,
          current.endDay ?? current.day
        );
        const endDay = Math.max(
          current.startDay ?? current.day,
          current.endDay ?? current.day
        );
        const patch = {
          startSlot: current.startSlot,
          endSlot: current.endSlot,
        };
        if (startDay === endDay) {
          onPlanSelectionChange({
            calendarDayPatch: { day: planDayKey(weekStart, startDay), ...patch },
          });
        } else {
          onPlanSelectionChange({
            calendarRangePatch: {
              startDay: planDayKey(weekStart, startDay),
              endDay: planDayKey(weekStart, endDay),
              ...patch,
            },
          });
        }
      } else {
        setAppointment({
          day: current.day,
          startSlot: current.startSlot,
          endSlot: current.endSlot,
        });
      }
      setDragState(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const handleDayPointerDown = (e, dayIdx) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest(".ra-event")) return;
    const slotH = getSlotH();
    const r = dayRefs.current[dayIdx].getBoundingClientRect();
    const y = e.clientY - r.top;
    const slot = clamp(Math.floor(y / slotH), 0, TOTAL_SLOTS - 1);
    const span = durationSlots != null ? durationSlots : 2;
    const startSlot = durationSlots != null ? clamp(slot, 0, TOTAL_SLOTS - span) : slot;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
    startDrag({
      mode: "create",
      startDay: dayIdx,
      anchorSlot: slot,
      day: dayIdx,
      startSlot,
      endSlot: startSlot + span,
      ...(planSelectionMode ? { startDay: dayIdx, endDay: dayIdx } : {}),
    });
    e.preventDefault();
  };

  const handleDraftPointerDown = (e, dayIndex, mode, apt) => {
    if ((e.button != null && e.button !== 0) || !apt) return;
    const slotH = getSlotH();
    const r = dayRefs.current[dayIndex].getBoundingClientRect();
    const y = e.clientY - r.top;
    const slot = Math.floor(y / slotH);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_err) {
      /* ignore */
    }
    startDrag({
      mode,
      startDay: dayIndex,
      endDay: dayIndex,
      anchorSlot: slot,
      day: dayIndex,
      origStart: apt.startSlot,
      startSlot: apt.startSlot,
      endSlot: apt.endSlot,
    });
    e.preventDefault();
    e.stopPropagation();
  };

  const liveApt = dragState
    ? {
        day: dragState.day,
        startDay: dragState.startDay ?? dragState.day,
        endDay: dragState.endDay ?? dragState.day,
        startSlot: dragState.startSlot,
        endSlot: dragState.endSlot,
      }
    : appointment;

  const planDays = planSelectionMode && planSelection?.days?.length
    ? planSelection.days
    : [];
  const planSlots = planSelectionMode && planSelection
    ? { startSlot: planSelection.startSlot, endSlot: planSelection.endSlot }
    : null;

  const showPlanBlockOnDay = (dayIndex) => {
    if (dragState && planSelectionMode) {
      const startDay = Math.min(
        dragState.startDay ?? dragState.day,
        dragState.endDay ?? dragState.day
      );
      const endDay = Math.max(
        dragState.startDay ?? dragState.day,
        dragState.endDay ?? dragState.day
      );
      return dayIndex >= startDay && dayIndex <= endDay;
    }
    const dayKey = planDayKey(weekStart, dayIndex);
    if (planDays.includes(dayKey) && getDaySchedule(planSelection, dayKey)) {
      return true;
    }
    return false;
  };

  const aptForDay = (dayIndex) => {
    const dayKey = planDayKey(weekStart, dayIndex);
    if (dragState && planSelectionMode) {
      const dragDay = dragState.day;
      const dragStart = dragState.startDay ?? dragDay;
      const dragEnd = dragState.endDay ?? dragDay;
      if (dayIndex >= dragStart && dayIndex <= dragEnd) {
        return {
          day: dayIndex,
          startSlot: dragState.startSlot,
          endSlot: dragState.endSlot,
        };
      }
      if (planDays.includes(dayKey)) {
        return getDaySchedule(planSelection, dayKey);
      }
      return null;
    }
    if (dragState && showPlanBlockOnDay(dayIndex)) {
      return {
        day: dayIndex,
        startSlot: dragState.startSlot,
        endSlot: dragState.endSlot,
      };
    }
    const daySlots = planSelectionMode
      ? getDaySchedule(planSelection, dayKey)
      : null;
    if (planDays.includes(dayKey) && daySlots) {
      return { day: dayIndex, ...daySlots };
    }
    if (!planSelectionMode && liveApt?.day === dayIndex) return liveApt;
    return null;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDayIdx = (() => {
    for (let i = 0; i < weekDayCount; i++) {
      const d = addDays(weekStart, i);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() === today.getTime()) return i;
    }
    return -1;
  })();

  const now = new Date();
  const nowSlot =
    now.getHours() >= START_HOUR && now.getHours() < END_HOUR
      ? (now.getHours() - START_HOUR) * 4 + now.getMinutes() / 15
      : null;

  const color = employee ? avatarColorForId(employee.id) : "#1f8ce8";

  return (
    <div
      className={`ra-cal-col${topBar ? " has-top-bar" : ""}${singleDay ? " ra-cal-col--single-day" : ""}${className ? ` ${className}` : ""}`}
    >
      {topBar ? <div className="ra-cal-topbar">{topBar}</div> : null}
      <div className="ra-cal-head">
        <div className="who">
          {employee ? (
            <>
              <div className="ra-emp-avatar" style={{ background: color }}>
                {initials(employee.displayTitle || employee.name)}
              </div>
              <div>
                <div>
                  <b>{employee.displayTitle || employee.name}</b>
                </div>
                <div className="role">
                  {t("report.agreement.redesign.jobs_this_week", {
                    count: existing.length,
                  })}
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="ra-cal-nav">
          <button type="button" onClick={() => onWeekChange(-1)} aria-label={t("report.agreement.redesign.prev_week")}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M7.5 2L3.5 6l4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onToday}
            style={{ borderColor: "var(--primary-200)", color: "var(--primary-700)" }}
          >
            {t("report.agreement.redesign.today")}
          </button>
          <button type="button" onClick={() => onWeekChange(1)} aria-label={t("report.agreement.redesign.next_week")}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M4.5 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="ra-week-label">{weekLabel}</span>
        </div>

        <div className="ra-legend">
          <span>
            <span className="swatch" style={{ background: "#e2e8f0" }} />
            {t("report.agreement.redesign.legend_existing")}
          </span>
          <span>
            <span className="swatch" style={{ background: "#fde68a" }} />
            {t("report.agreement.redesign.legend_absence")}
          </span>
          <span>
            <span className="swatch" style={{ background: "var(--primary)" }} />
            {t("report.agreement.redesign.legend_new")}
          </span>
        </div>
      </div>

      <div className="ra-cal-body">
        {isLoading ? (
          <div style={{ padding: 24, color: "var(--text-muted)", fontSize: 13 }}>
            {t("Loading events...")}
          </div>
        ) : (
          <div
            className={`ra-cal-grid${singleDay ? " ra-cal-grid--single-day" : ""}`}
            style={{
              gridTemplateColumns: `56px repeat(${visibleDayIndices.length}, 1fr)`,
            }}
          >
            <div
              className="ra-cal-day-head"
              style={{
                gridRow: 1,
                gridColumn: 1,
                borderRight: "1px solid var(--border)",
              }}
            />

            {visibleDayIndices.map((i, colOffset) => {
              const d = addDays(weekStart, i);
              const isToday = i === todayDayIdx;
              const isWeekend = i >= 5;
              const hasAbsence = absenceDays.includes(i);
              return (
                <div
                  key={`head-${i}`}
                  className={`ra-cal-day-head${isToday ? " today" : ""}${hasAbsence ? " has-absence" : ""}`}
                  style={{
                    gridRow: 1,
                    gridColumn: colOffset + 2,
                    ...(isWeekend ? { background: "#fafbfd" } : {}),
                  }}
                >
                  <div className="dow">{DAY_LABELS[i]}</div>
                  <div className="dnum">{d.getDate()}</div>
                  {hasAbsence && (
                    <div className="ra-absence-tag">
                      {t("report.agreement.redesign.absence_short")}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="ra-time-col" style={{ gridRow: 2, gridColumn: 1 }}>
              {Array.from({ length: END_HOUR - START_HOUR }).map((_, i) => (
                <div key={i} className="ra-time-cell">
                  {String(START_HOUR + i).padStart(2, "0")}:00
                </div>
              ))}
            </div>

            {visibleDayIndices.map((i, colOffset) => {
              const isWeekend = i >= 5;
              const isTodayCol = i === todayDayIdx;
              const hasAbsence = absenceDays.includes(i);
              const dayJobs = existing.filter((ev) => ev.day === i);
              const dayApt = aptForDay(i);
              const isLive = Boolean(dayApt);
              return (
                <div
                  key={`day-${i}`}
                  ref={(el) => {
                    dayRefs.current[i] = el;
                  }}
                  className={`ra-day-col${isWeekend ? " weekend" : ""}${isTodayCol ? " today" : ""}${hasAbsence ? " has-absence" : ""}`}
                  style={{
                    gridRow: 2,
                    gridColumn: colOffset + 2,
                    height: `calc(var(--slot-h) * ${TOTAL_SLOTS})`,
                    touchAction: "none",
                  }}
                  onPointerDown={(e) => handleDayPointerDown(e, i)}
                  role="presentation"
                >
                  {isTodayCol && nowSlot != null && (
                    <div
                      className="ra-now-line"
                      style={{ top: `calc(var(--slot-h) * ${nowSlot})` }}
                    />
                  )}

                  {dayJobs.map((ev) => (
                    <EventBlock
                      key={ev.id}
                      ev={ev}
                      conflict={
                        isLive &&
                        dayApt &&
                        (ev.kind === "absence"
                          ? true
                          : hasOverlap(ev, dayApt))
                      }
                    />
                  ))}

                  {isLive && dayApt && (
                    <DraftBlock
                      apt={dayApt}
                      onMoveDown={(e) =>
                        handleDraftPointerDown(e, i, "move", dayApt)
                      }
                      onResizeDown={(e) =>
                        handleDraftPointerDown(e, i, "resize", dayApt)
                      }
                      dragging={Boolean(dragState)}
                      t={t}
                      blockTitle={draftBlockTitle}
                      onRemove={
                        planSelectionMode &&
                        onRemovePlanDay &&
                        !dragState &&
                        planDays.includes(planDayKey(weekStart, i))
                          ? () => onRemovePlanDay(planDayKey(weekStart, i))
                          : null
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
